import {ResStructureType} from "@shapediver/sdk.geometry-api-sdk-v2";
import {
	ISelectionParameterPropsJsonSchema,
	PARAMETER_TYPE,
	PARAMETER_VISUALIZATION,
	TAG3D_JUSTIFICATION,
} from "@shapediver/viewer.session";
import {
	ATTRIBUTE_VISUALIZATION,
	CAMERA_TYPE,
} from "@shapediver/viewer.shared.types";
import {z} from "zod";
import {
	AppBuilderContainerNameType,
	AttributeVisualizationVisibility,
	FormWidgetSubmitBehavior,
	SavedStatesVisualization,
	SelectComponentType,
} from "./appbuilder";

// Zod type definition for SelectComponentType
const selectComponentTypes = [
	"buttonflex",
	"buttongroup",
	"chipgroup",
	"dropdown",
	"color",
	"imagedropdown",
	"fullwidthcards",
	"carousel",
	"grid",
	"multiselect-checkboxes",
] as const satisfies readonly SelectComponentType[];

const SelectComponentTypeSchema = z.enum(selectComponentTypes);

// Zod type definition for ISelectComponentItemDataType
export const ISelectComponentItemDataTypeSchema = z.object({
	displayname: z.string().optional(),
	tooltip: z.string().optional(),
	description: z.string().optional(),
	imageUrl: z.string().optional(),
	color: z.string().optional(),
	hidden: z.boolean().optional(),
	data: z.record(z.string(), z.any()).optional(),
});

// Zod type definition for ISelectParameterSettings
const ISelectParameterSettingsSchema = z.object({
	type: SelectComponentTypeSchema.optional(),
	itemData: z
		.record(z.string(), ISelectComponentItemDataTypeSchema)
		.optional(),
	searchable: z.boolean().optional(),
	limit: z.number().int().positive().optional(),
	height: z.string().optional(),
});

export const validateSelectParameterSettings = (value: any) => {
	return ISelectParameterSettingsSchema.safeParse(value);
};

// Zod type definition for IStringParameterSelectSettings
const IStringParameterSelectSettingsSchema =
	ISelectParameterSettingsSchema.extend(
		z.object({
			items: z.array(z.string()).optional(),
			source: z.string().optional(),
		}).shape,
	);

// Zod type definition for IStringParameterSettings
const IStringParameterSettingsSchema = z.object({
	lines: z.number().int().positive().optional(),
	selectSettings: IStringParameterSelectSettingsSchema.optional(),
});

export const validateStringParameterSettings = (value: any) => {
	return IStringParameterSettingsSchema.safeParse(value);
};

// Zod type definition for INumberParameterSettings
const INumberParameterSettingsSchema = z.object({
	step: z.number().positive().optional(),
	marks: z
		.array(
			z.object({
				value: z.number(),
				label: z.string().optional(),
			}),
		)
		.optional(),
	restrictToMarks: z.boolean().optional(),
	min: z.number().optional(),
	max: z.number().optional(),
});

export const validateNumberParameterSettings = (value: any) => {
	return INumberParameterSettingsSchema.safeParse(value);
};

// Zod type definition for IAppBuilderParameterDefinition
const IAppBuilderParameterDefinitionSchema = z.object({
	id: z.string(),
	choices: z.array(z.string()).optional(),
	decimalplaces: z.number().optional(),
	defval: z.string(),
	expression: z.string().optional(),
	format: z.array(z.string()).optional(),
	min: z.number().optional(),
	max: z.number().optional(),
	umin: z.number().optional(),
	umax: z.number().optional(),
	vmin: z.number().optional(),
	vmax: z.number().optional(),
	interval: z.number().optional(),
	name: z.string(),
	type: z.nativeEnum(PARAMETER_TYPE),
	visualization: z.nativeEnum(PARAMETER_VISUALIZATION).optional(),
	structure: z.nativeEnum(ResStructureType).optional(),
	group: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.optional(),
	hint: z.string().optional(),
	order: z.number().optional(),
	tooltip: z.string().optional(),
	displayname: z.string().optional(),
	hidden: z.boolean(),
	settings: z.record(z.string(), z.any()).optional(),
	value: z.string().optional(),
	step: z.number().positive().optional(),
});

// Zod type definition for property "overrides" of IAppBuilderParameterRef
const IAppBuilderParameterOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		group: true,
		order: true,
		tooltip: true,
		hidden: true,
		settings: true,
		step: true,
	});

// Zod type definition for IAppBuilderParameterRef
const IAppBuilderParameterRefSchema = z.object({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderParameterOverridesSchema.optional(),
	disableIfDirty: z.boolean().optional(),
	acceptRejectMode: z.boolean().optional(),
});

// Zod type definition for property "overrides" of IAppBuilderExportRef
const IAppBuilderExportOverridesSchema = IAppBuilderParameterOverridesSchema;

// Zod type definition for IAppBuilderExportRef
const IAppBuilderExportRefSchema = z.object({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderExportOverridesSchema.optional(),
});

// Zod type definition for IAppBuilderImageRef
export const IAppBuilderImageRefSchema = z.object({
	export: IAppBuilderExportRefSchema.pick({
		name: true,
		sessionId: true,
	}).optional(),
	href: z.string().optional(),
});

// Zod type definition for IAppBuilderParameterValueSourcePropsScreenshot
const IAppBuilderParameterValueSourcePropsScreenshotSchema = z.object({
	contentType: z.string().optional(),
	quality: z.number().min(0).max(1).optional(),
	resolution: z
		.object({
			width: z.number().int().positive(),
			height: z.number().int().positive(),
		})
		.optional(),
	// check if there is either a name or type present
	camera: z
		.union([
			z
				.object({
					name: z.string(),
				})
				.passthrough(),
			z
				.object({
					type: z.nativeEnum(CAMERA_TYPE),
				})
				.passthrough(),
		])
		.optional(),
});

// Zod type definition for IAppBuilderParameterValueSourcePropsDataOutput
const IAppBuilderParameterValueSourcePropsDataOutputSchema = z.object({
	sessionId: z.string().optional(),
	name: z.string(),
});

// Zod type definition for IAppBuilderParameterValueSourcePropsExport
const IAppBuilderParameterValueSourcePropsExportSchema = z.object({
	sessionId: z.string().optional(),
	name: z.string(),
	parameterValues: z
		.record(
			z.string(),
			z
				.string()
				.or(z.number())
				.or(z.boolean())
				.or(
					z.lazy(
						(): z.ZodType<any> =>
							IAppBuilderParameterValueSourceDefinitionSchema,
					),
				),
		)
		.optional(),
});

// Zod type definition for IAppBuilderParameterValueSourcePropsSdtf
const IAppBuilderParameterValueSourcePropsSdtfSchema = z.object({
	sessionId: z.string().optional(),
	name: z.string(),
	chunk: z
		.object({
			id: z.string().optional(),
			name: z.string().optional(),
		})
		.optional(),
});

// Zod type definition for IAppBuilderActionPropsCreateModelState
const IAppBuilderActionPropsCreateModelStateSchema = z.object({
	includeImage: z.boolean().optional(),
	image: IAppBuilderImageRefSchema.optional(),
	includeGltf: z.boolean().optional(),
	parameterNamesToInclude: z.array(z.string()).optional(),
	parameterNamesToExclude: z.array(z.string()).optional(),
});

// Zod type definition for IAppBuilderParameterValueSourcePropsModelState
const IAppBuilderParameterValueSourcePropsModelStateSchema =
	IAppBuilderActionPropsCreateModelStateSchema.extend({
		updateUrl: z.boolean().optional(),
	});

// Zod type definition for IAppBuilderParameterValueSourceDefinition
const IAppBuilderParameterValueSourceDefinitionSchema = z.discriminatedUnion(
	"type",
	[
		z.object({
			type: z.literal("dataOutput"),
			props: IAppBuilderParameterValueSourcePropsDataOutputSchema,
		}),
		z.object({
			type: z.literal("export"),
			props: IAppBuilderParameterValueSourcePropsExportSchema,
		}),
		z.object({
			type: z.literal("modelState"),
			props: IAppBuilderParameterValueSourcePropsModelStateSchema,
		}),
		z.object({
			type: z.literal("screenshot"),
			props: IAppBuilderParameterValueSourcePropsScreenshotSchema,
		}),
		z.object({
			type: z.literal("sdtf"),
			props: IAppBuilderParameterValueSourcePropsSdtfSchema,
		}),
	],
);

// Zod type definition for IAppBuilderActionPropsCommon
const IAppBuilderActionPropsCommonSchema = z.object({
	label: z.string().optional(),
	icon: z.string().optional(),
	tooltip: z.string().optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsCreateModelState
const IAppBuilderLegacyActionPropsCreateModelStateSchema =
	IAppBuilderActionPropsCreateModelStateSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsAddToCart
const IAppBuilderActionPropsAddToCartSchema = z
	.object({
		productId: z.string().optional(),
		quantity: z.number().optional(),
		price: z.number().optional(),
		description: z.string().optional(),
	})
	.extend(IAppBuilderActionPropsCreateModelStateSchema.shape);

// Zod type definition for IAppBuilderLegacyActionPropsAddToCart
const IAppBuilderLegacyActionPropsAddToCartSchema =
	IAppBuilderActionPropsAddToCartSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsSetParameterValue
const IAppBuilderActionPropsSetParameterValueSchema = z.object({
	parameter: IAppBuilderParameterRefSchema.pick({
		name: true,
		sessionId: true,
	}),
	value: z.string().optional(),
	source: IAppBuilderParameterValueSourceDefinitionSchema.optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsSetParameterValue
const IAppBuilderLegacyActionPropsSetParameterValueSchema =
	IAppBuilderActionPropsSetParameterValueSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsSetParameterValues
const IAppBuilderActionPropsSetParameterValuesSchema = z.object({
	parameterValues: z.array(
		IAppBuilderLegacyActionPropsSetParameterValueSchema,
	),
});

// Zod type definition for IAppBuilderLegacyActionPropsSetParameterValues
const IAppBuilderLegacyActionPropsSetParameterValuesSchema =
	IAppBuilderActionPropsSetParameterValuesSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsSetBrowserLocation
const IAppBuilderActionPropsSetBrowserLocationSchema = z.object({
	href: z.string().optional(),
	pathname: z.string().optional(),
	search: z.string().optional(),
	hash: z.string().optional(),
	target: z.enum(["_self", "_blank", "_parent", "_top"]).optional(),
});
// Zod type definition for IAppBuilderLegacyActionPropsSetBrowserLocation
const IAppBuilderLegacyActionPropsSetBrowserLocationSchema =
	IAppBuilderActionPropsSetBrowserLocationSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsCloseConfigurator
const IAppBuilderActionPropsCloseConfigurator = z.object({});

// Zod type definition for IAppBuilderLegacyActionPropsCloseConfigurator
const IAppBuilderLegacyActionPropsCloseConfiguratorSchema =
	IAppBuilderActionPropsCloseConfigurator.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

const IAppBuilderActionPropsCameraCommonSchema = z.object({
	camera: z
		.union([
			z
				.object({
					name: z.string(),
				})
				.passthrough(),
			z
				.object({
					type: z.nativeEnum(CAMERA_TYPE),
				})
				.passthrough(),
		])
		.optional(),
	options: z.record(z.string(), z.any()).optional(),
});

// Zod type definition for IAppBuilderActionPropsCameraCommon
const IAppBuilderActionPropsCameraSchema = z.discriminatedUnion("type", [
	z
		.object({
			type: z.literal("animate"),
			props: z
				.object({
					path: z.array(
						z.object({
							position: z.array(z.number()).length(3),
							target: z.array(z.number()).length(3),
						}),
					),
					startFromCurrent: z.boolean().optional(),
				})
				.extend(IAppBuilderActionPropsCameraCommonSchema.shape),
		})
		.extend(IAppBuilderActionPropsCommonSchema.shape),
	z
		.object({
			type: z.literal("assign"),
			props: z
				.object({})
				.extend(IAppBuilderActionPropsCameraCommonSchema.shape),
		})
		.extend(IAppBuilderActionPropsCommonSchema.shape),
	z
		.object({
			type: z.literal("set"),
			props: z
				.object({
					position: z.array(z.number()).length(3),
					target: z.array(z.number()).length(3),
				})
				.extend(IAppBuilderActionPropsCameraCommonSchema.shape),
		})
		.extend(IAppBuilderActionPropsCommonSchema.shape),
	z
		.object({
			type: z.literal("reset"),
			props: z
				.object({})
				.extend(IAppBuilderActionPropsCameraCommonSchema.shape),
		})
		.extend(IAppBuilderActionPropsCommonSchema.shape),
	z
		.object({
			type: z.literal("zoomTo"),
			props: z
				.object({
					initialPosition: z.array(z.number()).length(3).optional(),
					initialTarget: z.array(z.number()).length(3).optional(),
					nameFilter: z.array(z.string()).optional(),
				})
				.extend(IAppBuilderActionPropsCameraCommonSchema.shape),
		})
		.extend(IAppBuilderActionPropsCommonSchema.shape),
]);

// Zod type definition for IAppBuilderActionPropsSound
const IAppBuilderActionPropsSoundSchema = z.object({
	href: z.string(),
	autoplay: z.boolean().optional(),
	loop: z.boolean().optional(),
	labelPlaying: z.string().optional(),
	iconPlaying: z.string().optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsSetParameterValues
const IAppBuilderLegacyActionPropsSoundSchema =
	IAppBuilderActionPropsSoundSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsMessageToParent
const IAppBuilderActionPropsMessageToParentSchema = z.object({
	type: z.string(),
	data: z.record(z.string(), z.unknown()).optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsMessageToParent
const IAppBuilderLegacyActionPropsMessageToParentSchema =
	IAppBuilderActionPropsMessageToParentSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionDefinition
const IAppBuilderLegacyActionDefinitionSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("createModelState"),
		props: IAppBuilderLegacyActionPropsCreateModelStateSchema,
	}),
	z.object({
		type: z.literal("addToCart"),
		props: IAppBuilderLegacyActionPropsAddToCartSchema,
	}),
	z.object({
		type: z.literal("setParameterValue"),
		props: IAppBuilderLegacyActionPropsSetParameterValueSchema,
	}),
	z.object({
		type: z.literal("setParameterValues"),
		props: IAppBuilderLegacyActionPropsSetParameterValuesSchema,
	}),
	z.object({
		type: z.literal("setBrowserLocation"),
		props: IAppBuilderLegacyActionPropsSetBrowserLocationSchema,
	}),
	z.object({
		type: z.literal("closeConfigurator"),
		props: IAppBuilderLegacyActionPropsCloseConfiguratorSchema,
	}),
	z.object({
		type: z.literal("camera"),
		props: IAppBuilderActionPropsCameraSchema,
	}),
	z.object({
		type: z.literal("sound"),
		props: IAppBuilderLegacyActionPropsSoundSchema,
	}),
	z.object({
		type: z.literal("messageToParent"),
		props: IAppBuilderLegacyActionPropsMessageToParentSchema,
	}),
]);

// Zod type definition for property "overrides" of IAppBuilderControlParameterRef
const IAppBuilderControlParameterRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
		settings: true,
		step: true,
	});

// Zod type definition for property "overrides" of IAppBuilderControlExportRef
const IAppBuilderControlExportRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
	});

// Zod type definition for property "overrides" of IAppBuilderControlOutputRef
const IAppBuilderControlOutputRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
	});

// Zod type definition for IAppBuilderControlParameterRef
const IAppBuilderControlParameterRefSchema = z.object({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderControlParameterRefOverridesSchema.optional(),
	disableIfDirty: z.boolean().optional(),
	acceptRejectMode: z.boolean().optional(),
});

// Zod type definition for IAppBuilderControlExportRef
const IAppBuilderControlExportRefSchema = z.object({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderControlExportRefOverridesSchema.optional(),
	parameterValues: z
		.array(IAppBuilderLegacyActionPropsSetParameterValueSchema)
		.optional(),
});

// Zod type definition for IAppBuilderActionDefinition
const IAppBuilderActionDefinitionSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("createModelState"),
		props: IAppBuilderActionPropsCreateModelStateSchema,
	}),
	z.object({
		type: z.literal("addToCart"),
		props: IAppBuilderActionPropsAddToCartSchema,
	}),
	z.object({
		type: z.literal("setParameterValue"),
		props: IAppBuilderActionPropsSetParameterValueSchema,
	}),
	z.object({
		type: z.literal("setParameterValues"),
		props: IAppBuilderActionPropsSetParameterValuesSchema,
	}),
	z.object({
		type: z.literal("setBrowserLocation"),
		props: IAppBuilderActionPropsSetBrowserLocationSchema,
	}),
	z.object({
		type: z.literal("closeConfigurator"),
		props: IAppBuilderActionPropsCloseConfigurator,
	}),
	z.object({
		type: z.literal("camera"),
		props: IAppBuilderActionPropsCameraSchema,
	}),
	z.object({
		type: z.literal("sound"),
		props: IAppBuilderActionPropsSoundSchema,
	}),
	z.object({
		type: z.literal("messageToParent"),
		props: IAppBuilderActionPropsMessageToParentSchema,
	}),
]);

// Zod type definition for IAppBuilderControlActionRef
const IAppBuilderControlActionRefSchema = z
	.object({
		definition: IAppBuilderActionDefinitionSchema,
	})
	.extend(IAppBuilderActionPropsCommonSchema.shape);

// Zod type definition for IAppBuilderControlOutputRef
const IAppBuilderControlOutputRefSchema = z.object({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderControlOutputRefOverridesSchema.optional(),
});

// Zod type definition for IAppBuilderControl
const IAppBuilderControlSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("parameter"),
		props: IAppBuilderControlParameterRefSchema,
	}),
	z.object({
		type: z.literal("export"),
		props: IAppBuilderControlExportRefSchema,
	}),
	z.object({
		type: z.literal("action"),
		props: IAppBuilderControlActionRefSchema,
	}),
	z.object({
		type: z.literal("output"),
		props: IAppBuilderControlOutputRefSchema,
	}),
]);

// Zod type definition for IAppBuilderWidgetPropsCommon
const IAppBuilderWidgetPropsCommonSchema = z.object({});

// Zod type definition for IAppBuilderWidgetPropsAccordion
const IAppBuilderWidgetPropsAccordionSchema = z
	.object({
		parameters: z.array(IAppBuilderParameterRefSchema).optional(),
		exports: z.array(IAppBuilderExportRefSchema).optional(),
		defaultGroupName: z.string().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsText
const IAppBuilderWidgetPropsTextSchema = z
	.object({
		text: z.string().optional(),
		markdown: z.string().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsImage
const IAppBuilderWidgetPropsImageSchema = z
	.object({
		anchor: z.string().optional(),
		alt: z.string().optional(),
		target: z.string().default("_blank"),
		isSvg: z.boolean().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape)
	.extend(IAppBuilderImageRefSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsRoundChart
const IAppBuilderWidgetPropsRoundChartSchema = z
	.object({
		name: z.string().optional(),
		style: z.enum(["pie", "donut"]),
		labels: z.boolean().optional(),
		legend: z.boolean().optional(),
		data: z.array(
			z.object({name: z.string(), value: z.number(), color: z.string()}),
		),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsChartPlotSettings
const IAppBuilderWidgetPropsChartPlotSettingsSchema = z.object({
	xaxis: z.boolean().optional(),
	xlabel: z.string().optional(),
	yaxis: z.boolean().optional(),
	ylabel: z.string().optional(),
	grid: z.enum(["none", "x", "y", "xy"]).optional(),
	dots: z.boolean().optional(),
	legend: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsChartDataSet
const IAppBuilderWidgetPropsChartDataSetSchema = z.object({
	keys: z.array(z.string()),
	series: z.array(
		z.object({
			name: z.string(),
			color: z.string(),
			values: z.array(z.number()),
		}),
	),
});

// Zod type definition for IAppBuilderWidgetPropsChartCommon
const IAppBuilderWidgetPropsChartCommonSchema = z
	.object({
		name: z.string().optional(),
		plotSettings: IAppBuilderWidgetPropsChartPlotSettingsSchema,
		data: IAppBuilderWidgetPropsChartDataSetSchema,
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsLineChart
const IAppBuilderWidgetPropsLineChartSchema = z
	.object({
		style: z
			.enum([
				"bump",
				"linear",
				"natural",
				"monotone",
				"step",
				"stepBefore",
				"stepAfter",
			])
			.optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsAreaChart
const IAppBuilderWidgetPropsAreaChartSchema = z
	.object({
		style: z
			.enum([
				"bump",
				"linear",
				"natural",
				"monotone",
				"step",
				"stepBefore",
				"stepAfter",
			])
			.optional(),
		type: z.enum(["default", "stacked", "percent", "split"]).optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsBarChart
const IAppBuilderWidgetPropsBarChartSchema = z
	.object({
		style: z
			.enum(["default", "stacked", "percent", "waterfall"])
			.optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

const IAppBuilderWidgetPropsAttributeVisualizationNumberGradientSchema =
	z.object({
		type: z.literal("number"),
		min: z.number().optional(),
		max: z.number().optional(),
		steps: z.nativeEnum(ATTRIBUTE_VISUALIZATION).or(
			z.array(
				z.object({
					value: z.number(),
					colorBefore: z.string(),
					colorAfter: z.string(),
				}),
			),
		),
	});

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualizationStringGradient
const IAppBuilderWidgetPropsAttributeVisualizationStringGradientSchema =
	z.object({
		type: z.literal("string"),
		defaultColor: z.string().optional(),
		labelColors: z.array(
			z.object({
				values: z.array(z.string()),
				color: z.string(),
			}),
		),
	});

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualizationGradient
const IAppBuilderWidgetPropsAttributeVisualizationGradientSchema = z.union([
	z.discriminatedUnion("type", [
		IAppBuilderWidgetPropsAttributeVisualizationNumberGradientSchema,
		IAppBuilderWidgetPropsAttributeVisualizationStringGradientSchema,
	]),
	z.nativeEnum(ATTRIBUTE_VISUALIZATION),
]);

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualization
const IAppBuilderWidgetPropsAttributeVisualizationSchema = z.object({
	title: z.string().optional(),
	tooltip: z.string().optional(),
	attributes: z
		.array(
			z
				.object({
					attribute: z.string(),
					gradient:
						IAppBuilderWidgetPropsAttributeVisualizationGradientSchema.optional(),
				})
				.or(z.string()),
		)
		.optional(),
	visualizationMode: z
		.nativeEnum(AttributeVisualizationVisibility)
		.optional(),
	showLegend: z.boolean().optional(),
	defaultGradient:
		IAppBuilderWidgetPropsAttributeVisualizationGradientSchema.optional(),
	initialAttribute: z.string().optional(),
	passiveMaterial: z
		.object({
			color: z.string().optional(),
			opacity: z.number().optional(),
		})
		.optional(),
	disableAttributeAnchors: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsActions
const IAppBuilderWidgetPropsActionsSchema = z.object({
	actions: z.array(IAppBuilderLegacyActionDefinitionSchema),
});

// Zod type definition for IAppBuilderWidgetPropsAgent
const IAppBuilderWidgetPropsAgentSchema = z.object({
	context: z.string().optional(),
	parameterNames: z.array(z.string()).optional(),
	parameterNamesExclude: z.array(z.string()).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsProgress
const IAppBuilderWidgetPropsProgressSchema = z.object({
	showPercentage: z.boolean().optional(),
	showOnComplete: z.boolean().optional(),
	showMessages: z.boolean().optional(),
	delayRemoval: z.number().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsSceneTreeExplorer
const IAppBuilderWidgetPropsSceneTreeExplorerSchema = z.object({});

// Zod type definition for IAppBuilderWidgetPropsDesktopClientSelection
const IAppBuilderWidgetPropsDesktopClientSelectionSchema = z.object({
	clientsFilter: z.array(z.string()).optional(),
	autoConnect: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsDesktopClientOutputs
const IAppBuilderWidgetPropsDesktopClientOutputsSchema = z.object({});

// Zod type definition for IAppBuilderWidgetPropsControls
const IAppBuilderWidgetPropsControlsSchema = z.object({
	controls: z.array(IAppBuilderControlSchema),
});

// Zod type definition for IAppBuilderWidgetPropsForm
const IAppBuilderWidgetPropsFormSchema = z.object({
	controls: z.array(IAppBuilderControlSchema).optional(),
	parameters: z.array(IAppBuilderParameterRefSchema).optional(),
	export: IAppBuilderControlExportRefSchema.optional(),
	submit: z.nativeEnum(FormWidgetSubmitBehavior).optional(),
	successMessage: z.string().optional(),
	errorMessage: z.string().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsAccordionUi
const IAppBuilderWidgetPropsAccordionUiSchema = z.object({
	items: z.array(
		z.object({
			value: z.string().optional(),
			name: z.string(),
			icon: z.string().optional(),
			tooltip: z.string().optional(),
			widgets: z.array(
				z.lazy((): z.ZodType<any> => IAppBuilderWidgetSchema),
			),
		}),
	),
	multiple: z.boolean().optional(),
	defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
	value: z.union([z.string(), z.array(z.string())]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsStackUi
const IAppBuilderWidgetPropsStackUiSchema = z.object({
	name: z.string(),
	icon: z.string().optional(),
	tooltip: z.string().optional(),
	widgets: z.array(z.lazy((): z.ZodType<any> => IAppBuilderWidgetSchema)),
});

// Zod type definition for IAppBuilderWidgetPropsSavedStates
const savedStatesVisualizationValues = [
	"buttonflex",
	"buttongroup",
	"chipgroup",
	"dropdown",
	"imagedropdown",
	"fullwidthcards",
	"carousel",
	"grid",
] as const satisfies readonly SavedStatesVisualization[];

const IAppBuilderWidgetPropsSavedStatesSchema = z.object({
	visualization: z.enum(savedStatesVisualizationValues).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsTableColumn
const IAppBuilderWidgetPropsTableColumnSchema = z.object({
	accessor: z.string(),
	title: z.string().optional(),
	sortable: z.boolean().optional(),
	searchable: z.boolean().optional(),
	width: z.union([z.number(), z.string()]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsTable
const IAppBuilderWidgetPropsTableSchema = z.object({
	caption: z.string().optional(),
	columns: z.array(IAppBuilderWidgetPropsTableColumnSchema),
	records: z.array(z.record(z.string(), z.unknown())),
	highlightOnHover: z.boolean().optional(),
	stickyHeader: z.boolean().optional(),
	striped: z.boolean().optional(),
	withColumnBorders: z.boolean().optional(),
	withRowBorders: z.boolean().optional(),
	withTableBorder: z.boolean().optional(),
	height: z.number().optional(),
	estimateRowHeight: z.number().optional(),
	overscan: z.number().optional(),
});

// Zod type definition for IAppBuilderWidget
const IAppBuilderWidgetSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("accordion"),
		props: IAppBuilderWidgetPropsAccordionSchema,
	}),
	z.object({
		type: z.literal("text"),
		props: IAppBuilderWidgetPropsTextSchema,
	}),
	z.object({
		type: z.literal("image"),
		props: IAppBuilderWidgetPropsImageSchema,
	}),
	z.object({
		type: z.literal("roundChart"),
		props: IAppBuilderWidgetPropsRoundChartSchema,
	}),
	z.object({
		type: z.literal("lineChart"),
		props: IAppBuilderWidgetPropsLineChartSchema,
	}),
	z.object({
		type: z.literal("areaChart"),
		props: IAppBuilderWidgetPropsAreaChartSchema,
	}),
	z.object({
		type: z.literal("barChart"),
		props: IAppBuilderWidgetPropsBarChartSchema,
	}),
	z.object({
		type: z.literal("actions"),
		props: IAppBuilderWidgetPropsActionsSchema,
	}),
	z.object({
		type: z.literal("attributeVisualization"),
		props: IAppBuilderWidgetPropsAttributeVisualizationSchema,
	}),
	z.object({
		type: z.literal("agent"),
		props: IAppBuilderWidgetPropsAgentSchema,
	}),
	z.object({
		type: z.literal("progress"),
		props: IAppBuilderWidgetPropsProgressSchema,
	}),
	z.object({
		type: z.literal("desktopClientSelection"),
		props: IAppBuilderWidgetPropsDesktopClientSelectionSchema,
	}),
	z.object({
		type: z.literal("desktopClientOutputs"),
		props: IAppBuilderWidgetPropsDesktopClientOutputsSchema,
	}),
	z.object({
		type: z.literal("controls"),
		props: IAppBuilderWidgetPropsControlsSchema,
	}),
	z.object({
		type: z.literal("form"),
		props: IAppBuilderWidgetPropsFormSchema,
	}),
	z.object({
		type: z.literal("accordionUi"),
		props: IAppBuilderWidgetPropsAccordionUiSchema,
	}),
	z.object({
		type: z.literal("sceneTreeExplorer"),
		props: IAppBuilderWidgetPropsSceneTreeExplorerSchema,
	}),
	z.object({
		type: z.literal("stackUi"),
		props: IAppBuilderWidgetPropsStackUiSchema,
	}),
	z.object({
		type: z.literal("savedStates"),
		props: IAppBuilderWidgetPropsSavedStatesSchema,
	}),
	z.object({
		type: z.literal("table"),
		props: IAppBuilderWidgetPropsTableSchema,
	}),
]);

// Zod type definition for IAppBuilderTab
const IAppBuilderTabSchema = z
	.object({
		name: z.string(),
		icon: z.string().optional(),
		tooltip: z.string().optional(),
		widgets: z.array(IAppBuilderWidgetSchema),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderAnchor3dContainerProperties
const IAppBuilderAnchor3dContainerPropertiesSchema = z.object({
	id: z.string(),
	location: z.tuple([z.number(), z.number(), z.number()]),
	allowPointerEvents: z.boolean().optional(),
	justification: z.nativeEnum(TAG3D_JUSTIFICATION).optional(),
	previewIcon: z.string().optional(),
	width: z.union([z.string(), z.number()]).optional(),
	height: z.union([z.string(), z.number()]).optional(),
	maxWidth: z.union([z.string(), z.number()]).optional(),
	maxHeight: z.union([z.string(), z.number()]).optional(),
	useContainer: z.boolean().optional(),
	useCloseButton: z.boolean().optional(),
	hideable: z.boolean().optional(),
	selectionProperties:
		ISelectionParameterPropsJsonSchema.optional() as unknown as z.ZodObject<any>,
	mobileFallback: z
		.object({
			disabled: z.boolean().optional(),
			previewIcon: z.string().optional(),
			container: z
				.enum([
					AppBuilderContainerNameType.Left,
					AppBuilderContainerNameType.Right,
					AppBuilderContainerNameType.Bottom,
					AppBuilderContainerNameType.Top,
				])
				.optional(),
		})
		.optional(),
});

// Zod type definition for IAppBuilderAnchor2dContainerProperties
const IAppBuilderAnchor2dContainerPropertiesSchema = z.object({
	id: z.string(),
	location: z.union([
		z.tuple([z.string(), z.string()]),
		z.tuple([z.number(), z.number()]),
	]),
	allowPointerEvents: z.boolean().optional(),
	justification: z.nativeEnum(TAG3D_JUSTIFICATION).optional(),
	previewIcon: z.string().optional(),
	useCloseButton: z.boolean().optional(),
	draggable: z.boolean().optional(),
	width: z.union([z.string(), z.number()]).optional(),
	height: z.union([z.string(), z.number()]).optional(),
	maxWidth: z.union([z.string(), z.number()]).optional(),
	maxHeight: z.union([z.string(), z.number()]).optional(),
	useContainer: z.boolean().optional(),
	selectionProperties:
		ISelectionParameterPropsJsonSchema.optional() as unknown as z.ZodObject<any>,
	mobileFallback: z
		.object({
			disabled: z.boolean().optional(),
			previewIcon: z.string().optional(),
			container: z
				.enum([
					AppBuilderContainerNameType.Left,
					AppBuilderContainerNameType.Right,
					AppBuilderContainerNameType.Bottom,
					AppBuilderContainerNameType.Top,
				])
				.optional(),
		})
		.optional(),
});

// Zod type definition for IAppBuilderContainer
const IAppBuilderContainerSchema = z.discriminatedUnion("name", [
	z
		.object({
			name: z.literal(AppBuilderContainerNameType.Anchor3d),
			props: IAppBuilderAnchor3dContainerPropertiesSchema,
			tabs: z.array(IAppBuilderTabSchema).optional(),
			widgets: z.array(IAppBuilderWidgetSchema).optional(),
		})
		.extend(IAppBuilderWidgetPropsCommonSchema.shape),
	z
		.object({
			name: z.literal(AppBuilderContainerNameType.Anchor2d),
			props: IAppBuilderAnchor2dContainerPropertiesSchema,
			tabs: z.array(IAppBuilderTabSchema).optional(),
			widgets: z.array(IAppBuilderWidgetSchema).optional(),
		})
		.extend(IAppBuilderWidgetPropsCommonSchema.shape),
	// all other container props should be empty or undefined
	z
		.object({
			name: z.enum([
				AppBuilderContainerNameType.Left,
				AppBuilderContainerNameType.Right,
				AppBuilderContainerNameType.Bottom,
				AppBuilderContainerNameType.Top,
			]),
			props: z.undefined().optional(),
			tabs: z.array(IAppBuilderTabSchema).optional(),
			widgets: z.array(IAppBuilderWidgetSchema).optional(),
		})
		.extend(IAppBuilderWidgetPropsCommonSchema.shape),
]);

const IAppBuilderOutputActionsPropsSetParameterValueSchema = z.object({
	parameter: z.string(),
	output: z.string(),
});

// Zod type definition for IAppBuilderInstances
const IAppBuilderInstancesSchema = z.object({
	sessionId: z.string(),
	slug: z.string().optional(),
	name: z.string().optional(),
	parameterValues: z
		.record(
			z.string(),
			z
				.string()
				.or(z.number())
				.or(z.boolean())
				.or(IAppBuilderParameterValueSourceDefinitionSchema),
		)
		.optional(),
	transformations: z.array(z.array(z.number())).optional(),
	outputActions: z
		.array(
			z.discriminatedUnion("type", [
				z.object({
					type: z.literal("setParameterValue"),
					props: IAppBuilderOutputActionsPropsSetParameterValueSchema,
				}),
			]),
		)
		.optional(),
});

// Zod type definition for IAppBuilder
const IAppBuilderSchema = z.object({
	version: z.literal("1.0"),
	parameters: z.array(IAppBuilderParameterDefinitionSchema).optional(),
	sessionId: z.string().optional(),
	containers: z.array(IAppBuilderContainerSchema),
	instances: z.array(IAppBuilderInstancesSchema).optional(),
});

export const validateAppBuilder = (value: any) => {
	return IAppBuilderSchema.safeParse(value);
};

// Zod type definition for IAppBuilderSettingsSession
const IAppBuilderSettingsSessionSchema = z.object({
	ticket: z.string().optional(),
	guid: z.string().optional(),
	modelViewUrl: z.string().optional(),
	jwtToken: z.string().optional(),
	id: z.string(),
	waitForOutputs: z.boolean().optional(),
	loadOutputs: z.boolean().optional(),
	excludeViewports: z.array(z.string()).optional(),
	initialParameterValues: z.record(z.string(), z.string()).optional(),
	slug: z.string().optional(),
	platformUrl: z.string().optional(),
	acceptRejectMode: z.boolean().optional(),
	modelStateId: z.string().optional(),
	instance: z.boolean().optional(),
	loadOnFirstUse: z.boolean().optional(),
	keepInStore: z.boolean().optional(),
});

// Zod type definition for IAppBuilderSettingsSettings
const IAppBuilderSettingsSettingsSchema = z.object({
	disableFallbackUi: z.boolean().optional(),
});

// Zod type definition for IAppBuilderSettingsJson
const IAppBuilderSettingsJsonSchema = z.object({
	version: z.literal("1.0"),
	sessions: z.array(IAppBuilderSettingsSessionSchema).optional(),
	settings: IAppBuilderSettingsSettingsSchema.optional(),
	themeOverrides: z.record(z.string(), z.any()).optional(),
	appBuilderOverride: IAppBuilderSchema.optional(),
});

export const validateAppBuilderSettingsJson = (value: any) => {
	return IAppBuilderSettingsJsonSchema.safeParse(value);
};
